import { httpCoreService } from "../services/HttpCoreService";
import type {
  MonitoringOversightStats,
  StudyMonitoringSummary,
  StudyMonitoringDetailsResponse,
  SiteMonitoringDetailsResponse,
} from "../types/monitoringOversight.type";
import type { ApiResponse } from "../types/auth.type";

export interface IMonitoringOversightRepository {
  getOversightStats(studyId?: string): Promise<MonitoringOversightStats>;
  getStudySummaries(): Promise<StudyMonitoringSummary[]>;
  getStudyMonitoringDetails(
    studyId: string,
  ): Promise<StudyMonitoringDetailsResponse>;
  getSiteMonitoringDetails(
    siteId: string,
  ): Promise<SiteMonitoringDetailsResponse>;
}

export class MonitoringOversightRepository implements IMonitoringOversightRepository {
  async getOversightStats(studyId?: string): Promise<MonitoringOversightStats> {
    const response: ApiResponse<MonitoringOversightStats> =
      await httpCoreService.get<MonitoringOversightStats>(
        "/monitoring/oversight/stats",
        {
          params: studyId ? { studyId } : undefined,
        },
      );

    if (!response.data) {
      throw new Error("Failed to fetch oversight stats");
    }

    return response.data;
  }

  async getStudySummaries(): Promise<StudyMonitoringSummary[]> {
    const response: ApiResponse<{ studies: StudyMonitoringSummary[] }> =
      await httpCoreService.get<{ studies: StudyMonitoringSummary[] }>(
        "/monitoring/oversight/studies",
      );

    if (!response.data?.studies) {
      throw new Error("Failed to fetch study summaries");
    }

    return response.data.studies;
  }

  async getStudyMonitoringDetails(
    studyId: string,
  ): Promise<StudyMonitoringDetailsResponse> {
    const response = await httpCoreService.get<
      StudyMonitoringDetailsResponse & { data?: StudyMonitoringDetailsResponse }
    >(`/monitoring/oversight/studies/${studyId}`);

    const raw = response.data;
    if (!raw) {
      throw new Error("Failed to fetch study monitoring details");
    }
    // Handle nested wrapper: some backends return { data: { study, sites, heatmapData } }
    const payload =
      raw.study !== undefined
        ? (raw as StudyMonitoringDetailsResponse)
        : (raw as { data?: StudyMonitoringDetailsResponse }).data;
    if (!payload?.study || !payload?.sites || !payload?.heatmapData) {
      throw new Error("Invalid study monitoring details response");
    }
    return payload;
  }

  async getSiteMonitoringDetails(
    siteId: string,
  ): Promise<SiteMonitoringDetailsResponse> {
    const response: ApiResponse<SiteMonitoringDetailsResponse> =
      await httpCoreService.get<SiteMonitoringDetailsResponse>(
        `/monitoring/oversight/sites/${siteId}`,
      );

    if (!response.data) {
      throw new Error("Failed to fetch site monitoring details");
    }

    return response.data;
  }
}
